/**
 * StateOverview — /states/overview
 * State governance workspace overview — aggregation hub
 */
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useStateContext } from "@/hooks/useStateContext";
import { RefreshCw, ChevronRight } from "lucide-react";
import NextActionPanel from "@/components/NextActionPanel";
import WorkspaceHeader from "@/components/WorkspaceHeader";
import { useDemoMode } from "@/hooks/useDemoMode";
import { DemoToggle, DemoBanner, DemoDataDisplay } from "@/components/DemoDataOverlay";
import client from "@/api/client";
import { SectionCard } from "@/components/ds";

export default function StateOverview() {
  const navigate = useNavigate();
  const { stateId, geography: geo, stateParams } = useStateContext();
  const { demoMode, toggleDemo, exitDemo } = useDemoMode("states");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    client.get(`/states/overview${stateParams}`)
      .then(r => setData(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const steps = [
    { label: "State Climate Snapshot",           path: "/states/climate-snapshot"   },
    { label: "Aggregated Projects & Actions",     path: "/states/aggregated-projects" },
    { label: "Climate & Circularity Credentials", path: "/states/credentials"        },
    { label: "SDG Contribution",                  path: "/states/sdg"                },
    { label: "Carbon Pathways & Article 6",       path: "/states/carbon-pathways"    },
    { label: "Programme Structuring (PoA/CPA)",   path: "/states/programmes"         },
    { label: "Labeling & Classification",         path: "/states/labeling"           },
    { label: "Reports & Regulatory Exports",      path: "/states/reports"            },
    { label: "Financing & Investment Pipeline",   path: "/states/financing"          },
  ];

  const agg = data?.aggregated_footprint || {};

  return (
    <div className="p-6 lg:p-8 space-y-6">
      {/* Workspace header — consistent with other workspaces */}
      <WorkspaceHeader
        workspace="states"
        entityName={demoMode ? `${geo || "Lagos"} State (Demo)` : `${geo || "State"} Governance`}
        subTitle={demoMode
          ? "Multi-sector · Nigeria · Example Data"
          : "Aggregation · PoA/CPA · Article 6 · Financing Pipeline"}
        stats={loading ? [] : [
          { label: "Organizations",    value: (agg.org_count || 0).toString(),                              color: "text-teal-400"    },
          { label: "Total Footprint",  value: `${(agg.total_sme_tco2e || 0).toFixed(0)} tCO₂e`,            color: "text-amber-400"   },
          { label: "Projects",         value: (agg.project_count || 0).toString(),                          color: "text-blue-400"    },
          { label: "Reduction Pot.",   value: `${(agg.total_reduction_potential_tco2e || 0).toFixed(0)} t`, color: "text-emerald-400" },
        ]}
      >
        <div className="mt-2">
          <DemoToggle workspace="states" demoMode={demoMode} onToggle={toggleDemo} />
        </div>
      </WorkspaceHeader>

      {demoMode && <DemoBanner workspace="states" onExit={exitDemo} />}
      {demoMode && <DemoDataDisplay workspace="states" />}

      {!demoMode && (
        <>
          {/* Next action panel */}
          <NextActionPanel workspace="states" stateId={stateId} className="mb-2" />

          {/* Module Navigation */}
          <SectionCard title="Governance Modules" noPadding>
            {steps.map((step, i) => (
              <button key={step.label} onClick={() => navigate(step.path)}
                className="w-full flex items-center justify-between px-5 py-3.5 hover:bg-slate-50 border-b border-slate-50 last:border-0 transition-colors">
                <div className="flex items-center gap-3">
                  <span className="text-xs font-bold w-5 h-5 rounded-full bg-slate-100 text-slate-500 flex items-center justify-center shrink-0">
                    {i + 2}
                  </span>
                  <p className="text-sm font-medium text-slate-700">{step.label}</p>
                </div>
                <ChevronRight size={14} className="text-slate-400" />
              </button>
            ))}
          </SectionCard>

          <div className="bg-amber-50 border border-amber-200 rounded-xl px-5 py-3">
            <p className="text-xs text-amber-700 italic">
              States workspace aggregates DeCarbonIQ organizational data by geography.
              It is a governance-layer workspace — not an official government inventory.
              All Article 6 assessments are indicative only.
            </p>
          </div>
        </>
      )}
    </div>
  );
}
